import { FC, memo, MouseEvent } from 'react';
import styled from 'styled-components';
import { Entry } from '../services/DataService';

type CustomListItemProps = {
    data: Entry;
    onClick: (id: number, selected: boolean) => void;
};

type Selected = boolean | undefined;

const Wrapper = styled.div<{ selected: Selected }>`
    padding: 15px;
    cursor: pointer;
    user-select: none;
    background-color: ${({ theme, selected }) =>
        selected
            ? theme.palette.background.selected
            : theme.palette.background.paper};
    border-radius: 5px;
`;

const Step = styled.span`
    font-size: 0.75rem;
    padding: 2px;
    background-color: ${({ theme }) => theme.palette.text.primary};
    min-width: 30px;
    text-align: center;
    color: black;
    border-radius: 10px;
    font-weight: 700;
    font-family: monospace;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    display: flex;
`;

const Title = styled.div`
    flex: 1;
    margin: auto;
    text-align: right;
    font-size: 0.85rem;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const Description = styled.div`
    text-align: justify;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const Divider = styled.hr`
    display: block;
    height: 1px;
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
    margin: 10px 0;
    padding: 0;
`;

const HtmlRender = styled.span`
    b {
        font-weight: 700;
        color: ${({ theme }) => theme.palette.text.secondary};
    }
`;

const areEqual = (
    prevProps: CustomListItemProps,
    nextProps: CustomListItemProps
): boolean => {
    return prevProps.data.selected === nextProps.data.selected;
};

const CustomListItem: FC<CustomListItemProps> = ({ data, onClick }) => {
    const handleClick = (entry: Entry) => (e: MouseEvent) => {
        e.preventDefault();
        onClick(entry.id, !entry.selected);
    };
    return (
        <Wrapper onClick={handleClick(data)} selected={data.selected}>
            <Container>
                <Header>
                    <Step>{data.id}</Step>
                    <Title>Location: {data.location}</Title>
                </Header>
                <Divider />
                <Description>
                    <HtmlRender
                        dangerouslySetInnerHTML={{ __html: data.description }}
                    />
                </Description>
            </Container>
        </Wrapper>
    );
};

export default memo(CustomListItem, areEqual);
